"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.cleanFormattedString = cleanFormattedString;
exports.isIncompleteNumber = isIncompleteNumber;
exports.isValidNumber = isValidNumber;
exports.parseNumber = parseNumber;
var _bignumber = require("bignumber.js");
/**
 * Cleans the input string from extra characters (e.g. thousand separators) to prepare it for parsing
 * @param {string} value Raw user's input
 * @param {{ decimal: string; thousands: string }} delimiters
 * @param {boolean} [allowIncomplete] Skips from parsing the input if it's incomplete (e.g. has zeros at the end)
 * @returns {string|null} Cleaned user input (`null` in case of invalid number value)
 */
function cleanFormattedString(value, delimiters) {
  var allowIncomplete = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var inputValue = value.trim();
  // Escape special regex characters in the thousand separator
  var escapedThousandSeparator = escapeRegExp(delimiters.thousands);

  // Create a regex to match all thousand separators in the input
  var thousandSeparatorRegex = new RegExp(escapedThousandSeparator, 'g');

  // Remove all thousand separators
  inputValue = inputValue.replace(thousandSeparatorRegex, '');

  // Replace decimal separator with '.'
  if (delimiters.decimal !== '.') {
    var escapedDecimalSeparator = escapeRegExp(delimiters.decimal);
    var decimalSeparatorRegex = new RegExp(escapedDecimalSeparator, 'g');
    inputValue = inputValue.replace(decimalSeparatorRegex, '.');
  }

  // Remove non-numeric characters (excluding '.' and '-')
  inputValue = inputValue.replace(/[^0-9.,+-]/g, '');

  // Handle multiple decimal points
  var parts = inputValue.split('.');
  if (parts.length > 2) {
    inputValue = parts[0] + '.' + parts.slice(1).join('');
  }

  // Remove all negative signs not at the start
  inputValue = inputValue.replace(/(?!^)-/g, '');

  // If multiple negative signs at the start, keep only one
  inputValue = inputValue.replace(/^-+/, '-');

  // Remove leading zeros
  inputValue = inputValue.replace(/^(-?)0+(?!$)/, '$1');
  return allowIncomplete && isIncompleteNumber(inputValue) || !isValidNumber(inputValue) ? null : inputValue;
}

/**
 * Ensures special characters in a string are properly escaped
 * so that the string can be safely used in a regular expression pattern.
 * @param {string} string The string to escape
 * @returns {string} The escaped string
 */
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Parses a string value into a number or BigNumber
 * @param {string} value Input value (already cleaned)
 * @param {boolean} [isBigNumber] Whether the number should be converted to BigNumber
 * @returns {number|BigNumber|null} Parsed value
 */
function parseNumber(value) {
  var isBigNumber = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  try {
    if (isBigNumber) {
      var parsedValue = new _bignumber.BigNumber(value);
      return parsedValue.isFinite() ? parsedValue : null; // Handle NaN, Infinity
    }
    return parseFloat(value);
  } catch (error) {
    // Parsing failed, possibly due to incomplete input
    return null;
  }
}

/**
 * Validates the string for a valid number to be parsed.
 * @param {string} value Cleaned string to check
 * @returns {boolean} True if value is a valid number, false otherwise
 */
function isValidNumber(value) {
  return typeof value === 'string' && /^-?(?:\d+\.\d+|\d+|\.\d+)$/.test(value);
}
function isIncompleteNumber(value) {
  return typeof value === 'string' && /^-?(?:[1-9]\d*\.[1-9]*0+$|0\.[1-9]*0+$|\.\d*0+|0\.)$|^-$|^\.$/.test(value);
}